import { memo, useMemo, useState } from "react";
import { Handle, Position } from "reactflow";
import "./nodeStyles.css";
import { Avatar, Button, Input, List, Popover } from "antd";
import Option from "../../assets/icons/Menu.svg";
import Search from "../../assets/icons/Search.svg";
import { Image } from "react-bootstrap";
import { useGetLinkModelsListQuery } from "../../service/apiSlices/workbench";
import useDebounce from "../../hooks/useDebounce";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import DeleteConfirmModel from "../dashboard/DashboardMain/DeleteOpenModel/DeleteConfirmModel";
import { findPreviousNode, processString } from "../../utilities/Nodes/getConnectedChildNodes";
import { useTranslation } from "react-i18next";
import { handleDeLinkNode, handleRemoveSingleConnected } from "../../utilities/Nodes/commonFunctions";
import { LinkNodeProps } from "../../utilities/types/common";
import { calculatePopoverStyles } from "../../utilities/utils";
import { useFlowDataContext } from "../../context/workbench/FlowDataContext";
import { useViewPortContext } from "../../context/workbench/ViewPortContext";
import { usePermission } from "../../hooks/usePermission";

const LinkNode = ({ data, id }: LinkNodeProps) => {
    const { t } = useTranslation("common");
    const { profileName, profileImage } = useSelector((state: any) => state.static);
    const location = useLocation();
    const flowDataContext = useFlowDataContext();
    const ViewPortContext = useViewPortContext();
    const { canView, canEdit } = usePermission();

    const [searchValue, setSearchValue] = useState<any>("");
    const [open, setOpen] = useState<boolean>(false);
    const [isDeleteModelOpen, setIsDeleteModelOpen] = useState<boolean>(false);
    const [keyValue, setKeyValue] = useState<any>(data?.content || "");
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const debouncedSearchValue = useDebounce(searchValue);
    const previousNode = useMemo(() => findPreviousNode(id, flowDataContext?.edges, flowDataContext?.nodes), [id, flowDataContext?.edges, flowDataContext?.nodes]);

    const { data: linkModelLists } = useGetLinkModelsListQuery({
        search: debouncedSearchValue,
    });

    const handleVisibleChange = (newVisible: boolean) => {
        setOpen(newVisible);
    };

    const handleContentUpdate = (key: any) => {
        setKeyValue(key);
        setIsEditing(false);
        if (typeof data?.onContentUpdate === "function") {
            data.onContentUpdate(id, key); // Update parent node's content
        } else {
            console.error("onContentUpdate is not defined or not a function");
        }
    };
    const value = linkModelLists?.data?.find((res: any) =>
        keyValue?.includes(res?.secret_key)
    );

    const actionContent = (
        <div className="card-action-options">
            <List className="w-100">
                {keyValue !== "" && !isEditing && (
                    <List.Item>
                        <Button
                            className="action-btn"
                            onClick={() => {
                                setOpen(false);
                                setIsEditing(true);
                            }}
                        >
                            {t("buttons.replace")}
                        </Button>
                    </List.Item>
                )}
                <List.Item>
                    <Button
                        className="action-btn"
                        onClick={() => {
                            setOpen(false);
                            setIsDeleteModelOpen(true);
                        }}
                    >
                        {t("buttons.delete")}
                    </Button>
                </List.Item>
                {previousNode !== null && (
                    <List.Item>
                        <Button className="action-btn" onClick={() => { handleDeLinkNode(data, id); setOpen(false); }}>
                            {t("buttons.delink")}
                        </Button>
                    </List.Item>
                )}
            </List>
        </div>
    );

    const UpdatelinkModelLists = linkModelLists?.data?.filter(
        (value: any) => !location?.state?.secret_key?.includes(value?.secret_key)
    );

    // Calculate popover styles based on zoom level
    const popoverStyle = calculatePopoverStyles(ViewPortContext?.workbenchZoomLevel);

    return (
        <div className="node link-node">
            <div className="node-header d-flex justify-content-between align-items-center">
                <div className="card border-0 w-100 node-inner-card bg-transparent p-0">
                    <div className="card-header bg-transparent border-0 p-0">
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center gap-2 mt-2">
                                {profileImage ? <Avatar src={profileImage} size={"default"} /> : <Avatar>{processString(profileName)}</Avatar>}
                            </div>
                            <div>
                                {canView && canEdit && (
                                    <Popover
                                        content={actionContent}
                                        trigger="hover"
                                        arrow={false}
                                        open={open}
                                        placement="right"
                                        overlayClassName="add-node-popover"
                                        onOpenChange={handleVisibleChange}
                                        overlayInnerStyle={popoverStyle.overlayInnerStyle}
                                        overlayStyle={popoverStyle.overlayStyle}
                                    >
                                        <Button className="action-btn">
                                            <Image src={Option} alt="Option" />
                                        </Button>
                                    </Popover>
                                )}
                            </div>
                        </div>
                        <hr className="divider" />
                    </div>
                    <div className="card-body p-0">
                        {(keyValue === "" || isEditing) && (
                            <Input
                                type="search"
                                className="link-model-search-input"
                                placeholder="Search model"
                                name="link-model"
                                prefix={<img src={Search} alt="search" />}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        )}
                        <div className="scrollable-list mt-3 nowheel">
                            {keyValue !== "" && !isEditing ? (
                                <div className="list-name-link1">{value?.filename}</div>
                            ) : (
                                <>
                                    {UpdatelinkModelLists?.length > 0 ? (
                                        <>
                                            {UpdatelinkModelLists?.map((res: any, index: any) => (
                                                <div key={index}>
                                                    <div
                                                        className="list-name-link"
                                                        onClick={() => handleContentUpdate(res?.secret_key)}
                                                    >
                                                        {res?.filename}
                                                    </div>
                                                    {index < UpdatelinkModelLists?.length - 1 && (
                                                        <hr className="hr-list" />
                                                    )}
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        <p className="list-name-link text-center mt-4">
                                            {t("noModelDataFound")}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Handle type="source" position={Position.Right} className="handle" />
            <Handle type="target" position={Position.Left} className="handle" />
            <DeleteConfirmModel
                visible={isDeleteModelOpen}
                onConfirm={() => { handleRemoveSingleConnected(data, id); setOpen(false); }}
                onCancel={() => setIsDeleteModelOpen(false)}
                title={t("deleteModal.title")}
                content={t("deleteModal.description")}
                okText={t("buttons.confirm")}
                cancelText={t("buttons.cancel")}
            />
        </div>
    );
};

export default memo(LinkNode);